<template>
  <form class="vl-form" @submit.prevent="onSubmit()">
    <vl-grid v-if="definition" v-vl-spacer:bottom.medium>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-properties-list v-if="definition">
          <vl-properties-label>{{ $t('productprocessdefinition.detail.id') }}</vl-properties-label>
          <vl-properties-data>{{ definition.id }}</vl-properties-data>
          <vl-properties-label>{{ $t('productprocessdefinition.detail.label') }}</vl-properties-label>
          <vl-properties-data>{{ definition.label }}</vl-properties-data>
          <vl-properties-label>{{ $t('productprocessdefinition.detail.organisation') }}</vl-properties-label>
          <vl-properties-data>{{ definition.organisation }} ({{ definition.ovoNumber }})</vl-properties-data>
        </vl-properties-list>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-form-column v-if="hasParent" v-vl-spacer:top.small>
          <PbsRadioButtonField name="importData" :label="$t('productprocessdefinition.actions.dryrun.importData')" />
        </vl-form-column>
        <vl-form-column v-if="hasParent" v-vl-spacer:top.small>
          <PbsRadioButtonField
            name="importMetadata"
            :label="$t('productprocessdefinition.actions.dryrun.importMetadata')"
          />
        </vl-form-column>
        <vl-form-column v-vl-spacer:top.small>
          <PbsRadioButtonField
            name="includeTriggeredProductDefinitions"
            :label="$t('productprocessdefinition.actions.dryrun.includeTriggeredProductDefinitions')"
          />
        </vl-form-column>
      </vl-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="isSaving" :mod-loading="isSaving">{{
            $t('productprocessdefinition.actions.dryrun.label')
          }}</vl-button>
          <vl-button :mod-disabled="isSaving" mod-icon-before mod-secondary tag-name="a" @click.prevent="cancel">
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <template v-if="isLoading">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('general.formLoading')" />
        </div>
      </vl-region>
    </template>
    <template v-if="error">
      <vl-region>
        <vl-alert icon="warning" :title="$t('general.formLoadingError')" :content="error" mod-naked mod-error />
      </vl-region>
    </template>
  </form>
</template>
<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useProductProcessDefinitionDetailStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-detail.store';
import { useProductRunStore } from '@/modules/configuration/store/product-run/product-run.store';
import { reactive } from 'vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useForm } from 'vee-validate';
import { boolean, object } from 'yup';
import PbsRadioButtonField from '@/components/forms/PbsRadioButtonField.vue';
import { useProductEntityDataMappingStore } from '@/modules/configuration/store/product-entity-data-mapping/product-entity-data-mapping.store';
import { parseException } from '@/modules/core/store/alert.store';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const detailStore = useProductProcessDefinitionDetailStore();
const productEntityDataMappingStore = useProductEntityDataMappingStore();
const runStore = useProductRunStore();

const props = defineProps({
  id: {
    type: Number,
    required: true
  }
});

const validationSchema = object({
  importData: boolean().test('has-data-migration', t('productprocessdefinition.actions.dryrun.noMigration'), (value) =>
    value ? data.hasDataMigration : true
  ),
  importMetadata: boolean(),
  includeTriggeredProductDefinitions: boolean()
});

const { handleSubmit, resetForm } = useForm({ validationSchema });

const data = reactive({
  definition: null,
  hasDataMigration: null,
  isSaving: false,
  isLoading: false,
  error: null
});

onMounted(async () => {
  try {
    data.isLoading = true;
    await loadProductEntityDataMapping();
    await loadProductProcessDefinition();
  } catch (error) {
    data.error = parseException(error);
  } finally {
    data.isLoading = false;
  }

  setInitialFormValues();
});

const setInitialFormValues = () => {
  resetForm({
    values: {
      importData: false,
      importMetadata: false,
      includeTriggeredProductDefinitions: true
    }
  });
};

const loadProductProcessDefinition = async () => {
  const productProcessDefinition = await detailStore.getDetail(props.id);
  if (productProcessDefinition) {
    data.definition = productProcessDefinition;
  }
};

const loadProductEntityDataMapping = async () => {
  const productEntityDataMapping = await productEntityDataMappingStore.getByDefinition(props.id);
  data.hasDataMigration = productEntityDataMapping?.length > 0;
};

const hasParent = computed(() => {
  return data.definition?.parent?.id;
});

const isLoading = computed(() => {
  return data.isLoading;
});

const error = computed(() => {
  return data.error;
});

const isSaving = computed(() => {
  return data.isSaving;
});

const definition = computed(() => {
  return data.definition;
});

const onSuccess = async (values) => {
  await startdryrun(values.importData, values.importMetadata, values.includeTriggeredProductDefinitions);

  closeModal();
};

const startdryrun = async (importData: boolean, importMetadata: boolean, includeTriggeredProductDefinitions: boolean): Promise<void> => {
  try {
    data.isSaving = true;
    await runStore.startdryrun(props.id, importData, importMetadata, includeTriggeredProductDefinitions);
  } catch (e) {
    console.error(e);
  } finally {
    data.isSaving = false;
  }
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>
<style lang="scss" scoped></style>
